import { useEffect, useMemo, useState } from 'react';
import gamesService from '../../services/gamesService';
import { useAppSelector } from '../../store/hooks';
import { ALL_GAMES, type GameMeta, type GameType } from '../../types/games';

export function filterVisibleGames(gameTypes: GameType[]): GameMeta[] {
  const enabled = new Set(gameTypes);
  return ALL_GAMES.filter((game) => enabled.has(game.gameType));
}

export function useEnabledGames(appName: string | undefined) {
  const { isAuthenticated } = useAppSelector((state) => state.user);
  const [enabledGameTypes, setEnabledGameTypes] = useState<GameType[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!appName || !isAuthenticated) {
      setEnabledGameTypes([]);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    gamesService
      .getEnabledGames(appName)
      .then((res) => {
        if (active) setEnabledGameTypes(res.gameTypes || []);
      })
      .catch(() => {
        if (active) setEnabledGameTypes([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [appName, isAuthenticated]);

  const visibleGames = useMemo(
    () => filterVisibleGames(enabledGameTypes),
    [enabledGameTypes]
  );

  return {
    enabledGameTypes,
    visibleGames,
    loading,
    hasEnabledGames: enabledGameTypes.length > 0,
  };
}
